"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createReport = createReport;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
/** Map filesystem-illegal chars to a space; keep human spaces/parens; collapse + trim. */
function safeFileStem(s) {
    return s.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
}
/**
 * Persist an already-synthesized report as a standalone `report` artifact doc.
 *
 * A report is a **leaf snapshot** — deliberately NOT loaded into LoomState (see the
 * loom-ai-analysis design, storage decision A): being outside project state makes it
 * excluded-by-construction from refs / staleness / derived-status / requires_load.
 * It is a versioned markdown file on disk (born status "active", version 1), written
 * under `loom/reports/` (cross-weave) or `loom/{weave}/reports/` (weave-scoped). The
 * agent authors `content`; this use-case only writes the file. Shared by the MCP tool
 * and the CLI so both surfaces write byte-identical files.
 *
 * Frontmatter is deliberately minimal (kind + generated_at); scope + sources are
 * rendered as a body `## Provenance` section (design fork 1a), not frontmatter — the
 * canonical serializer is flat and would JSON-stringify a nested scope object.
 */
async function createReport(input, deps) {
    const loomRoot = deps.getActiveLoomRoot();
    const { kind, title, content } = input;
    const scope = input.scope ?? {};
    const sources = input.sources ?? [];
    const id = (0, dist_1.generateDocId)('report');
    const date = (0, dist_1.today)();
    const generatedAt = (0, dist_1.nowIso)();
    // The filename appends `({date})`, so a title that already ends in a date would
    // double it (e.g. "Overview (2026-07-12) (2026-07-12) - …"). Strip a trailing
    // `(YYYY-MM-DD)` from the title before using it — the prompt asks for a date-less
    // title, this is the belt-and-suspenders for a title that carries one anyway. The
    // date already lives in `created` / `generated_at`, so the title never needs it.
    const cleanTitle = title.replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/, '').trim() || title;
    const reportsDir = input.weaveSlug
        ? path.join(loomRoot, 'loom', input.weaveSlug, 'reports')
        : path.join(loomRoot, 'loom', 'reports');
    await deps.fs.ensureDir(reportsDir);
    const fileStem = safeFileStem(`${cleanTitle} (${date}) - ${kind} report`);
    const filePath = path.join(reportsDir, `${fileStem}.md`);
    const frontmatter = [
        '---',
        'type: report',
        `id: ${id}`,
        `title: "${cleanTitle.replace(/"/g, '\\"')}"`,
        'status: active',
        `created: ${date}`,
        'version: 1',
        'tags: []',
        'parent_id: null',
        'requires_load: []',
        `kind: ${kind}`,
        `generated_at: "${generatedAt}"`,
        '---',
        '',
    ];
    const weaves = scope.weaves?.length ? scope.weaves.join(', ') : 'all';
    const threads = scope.threads?.length ? scope.threads.join(', ') : 'all';
    const provenance = [
        '## Provenance',
        '',
        `- **Kind:** ${kind}`,
        `- **Scope:** weaves: ${weaves}; threads: ${threads}; from: ${scope.from ?? '—'}; to: ${scope.to ?? '—'}`,
        `- **Sources:** ${sources.length ? sources.join(', ') : '(roadmap)'}`,
        `- **Generated:** ${generatedAt}`,
    ].join('\n');
    const body = `${content.trimEnd()}\n\n${provenance}\n`;
    await deps.fs.writeFile(filePath, frontmatter.join('\n') + body, 'utf8');
    return { id, filePath, kind };
}
